import {Component, type ErrorInfo, type ReactNode} from "react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * ErrorBoundary — React requires a class component for this.
 * This is the only exception to the "no classes" rule.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = {hasError: false, error: null};

  static getDerivedStateFromError(error: Error): State {
    return {hasError: true, error};
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  handleReset = () => {
    this.setState({hasError: false, error: null});
  };

  override render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-6 p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-(--color-danger)/10 flex items-center justify-center">
            <svg
              className="w-8 h-8 text-(--color-danger)"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              role="img"
            >
              <title>Error</title>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
              />
            </svg>
          </div>

          <div>
            <h2 className="text-xl font-bold text-(--color-text-primary) mb-2">Algo salió mal</h2>
            <p className="text-sm text-(--color-text-secondary) max-w-md">
              Ocurrió un error inesperado. Puedes intentar recargar la página o volver al inicio.
            </p>
          </div>

          {this.state.error && (
            <pre className="text-xs text-(--color-text-tertiary) bg-(--color-surface-2) rounded-lg px-4 py-3 max-w-lg overflow-auto max-h-32">
              {this.state.error.message}
            </pre>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={this.handleReset}
              className="px-4 py-2 text-sm font-medium rounded-md bg-(--color-primary) text-white hover:opacity-90 cursor-pointer"
            >
              Reintentar
            </button>
            <a
              href="/dashboard"
              className="px-4 py-2 text-sm font-medium rounded-md border border-(--color-border) text-(--color-text-primary) hover:bg-(--color-surface-2)"
            >
              Ir al inicio
            </a>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
